import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Empty, Icon, Notice, Panel } from '../design/index.js'
import { useApi, type FeedEntry } from '../app/api.js'
import { ErrorBlock, Loading, messageFor } from './feedback.js'
import { formatDateTime } from './format.js'

/**
 * The Team Board (docs/02 §4a).
 *
 * Two streams in one list: what HAPPENED to the document, read from the audit
 * log, and what people SAID about it. The first is why the second is worth
 * having — "approved · then Ramesh said the rate was wrong" is a sentence you
 * can only read when both are on the same timeline.
 *
 * A remark can carry a file. Those are marked as coming from the board so the
 * drive's Files tab can leave them out of the drawing register.
 */

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read.'))
    reader.onload = () => {
      const result = String(reader.result ?? '')
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.readAsDataURL(file)
  })

export interface TeamBoardProps {
  readonly documentId?: string
  readonly projectId?: string
  readonly title?: string
}

export const TeamBoard = ({ documentId, projectId, title = 'Team board' }: TeamBoardProps) => {
  const api = useApi()
  const client = useQueryClient()
  const picker = useRef<HTMLInputElement | null>(null)

  const key = ['board', api.companyId, documentId ?? '', projectId ?? '']
  const feed = useQuery({
    queryKey: key,
    queryFn: () => api.board({
      ...(documentId ? { documentId } : {}),
      ...(projectId ? { projectId } : {}),
    }),
    enabled: Boolean(documentId || projectId),
  })

  const [body, setBody] = useState('')
  const [pending, setPending] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const post = useMutation({
    mutationFn: async () => api.postToBoard({
      ...(documentId ? { documentId } : {}),
      ...(projectId ? { projectId } : {}),
      body,
      ...(pending
        ? { file: { fileName: pending.name, contentType: pending.type, base64: await readAsBase64(pending) } }
        : {}),
    }),
    onSuccess: (rows) => {
      setError(null)
      setBody('')
      setPending(null)
      client.setQueryData(key, rows)
      // A board attachment lands in the drive too.
      void client.invalidateQueries({ queryKey: ['drive'] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.removeRemark(id.replace(/^remark:/, '')),
    onSuccess: () => { void client.invalidateQueries({ queryKey: key }) },
    onError: (err) => setError(messageFor(err)),
  })

  if (feed.isLoading) return <Loading />
  if (feed.error) return <ErrorBlock error={feed.error} />

  const entries = feed.data ?? []

  return (
    <Panel title={title}>
      {error ? <Notice kind="bad">{error}</Notice> : null}

      <div className="board-compose">
        <textarea
          className="control"
          style={{ minHeight: 76 }}
          placeholder="Say something about this — a question, a decision, why a rate is what it is."
          value={body}
          onChange={(event) => setBody(event.currentTarget.value)}
        />
        <div className="row row--wrap" style={{ marginTop: 8 }}>
          <input
            ref={picker}
            type="file"
            hidden
            onChange={(event) => {
              const file = event.currentTarget.files?.[0]
              event.currentTarget.value = ''
              if (file) setPending(file)
            }}
          />
          <Button kind="ghost" onClick={() => picker.current?.click()}>
            {pending ? `Attached: ${pending.name}` : 'Attach a file'}
          </Button>
          {pending ? (
            <Button kind="ghost" onClick={() => setPending(null)}>Remove it</Button>
          ) : null}
          <Button
            kind="primary"
            disabled={post.isPending || (body.trim() === '' && pending === null)}
            onClick={() => post.mutate()}
          >
            Post
          </Button>
        </div>
      </div>

      {entries.length === 0 ? (
        <Empty
          title="Nothing here yet"
          body={[
            'Everything that happens to this document appears here, along with anything anyone says about it.',
          ]}
        />
      ) : (
        <ol className="board">
          {entries.map((entry) => <BoardRow key={entry.id} entry={entry} onRemove={remove.mutate} api={api} />)}
        </ol>
      )}
    </Panel>
  )
}

const BoardRow = ({
  entry, onRemove, api,
}: {
  entry: FeedEntry
  onRemove: (id: string) => void
  api: ReturnType<typeof useApi>
}) => (
  <li className={entry.kind === 'REMARK' ? 'board__item board__item--remark' : 'board__item'}>
    <span className="board__mark" aria-hidden="true">
      <Icon name={entry.kind === 'REMARK' ? 'info' : 'check'} size={16} />
    </span>
    <div className="board__body">
      <div className="board__head">
        <span className="board__who">{entry.text}</span>
        <span className="board__when">{formatDateTime(entry.at)}</span>
      </div>
      {entry.body ? <p className="board__text">{entry.body}</p> : null}
      {entry.attachment ? (
        <a className="board__file" href={api.driveFileUrl(entry.attachment.id)}>
          <Icon name="clip" size={15} /> {entry.attachment.fileName}
        </a>
      ) : null}
      {entry.mine ? (
        <button type="button" className="linkish" onClick={() => onRemove(entry.id)}>
          Withdraw
        </button>
      ) : null}
    </div>
  </li>
)
